import { Stack } from '@mui/material'
import type { ActionPermissions } from '../../../../hooks/useVehiclesNavigoMachine/types'

import { ExpandButton } from '@/components/Navigo/components/ExpandButton'

import { BreadcrumbsButton } from './components/BreadcrumbsButton'
import { CenterOnMapButton } from './components/CenterOnMapButton'
import { EditButton } from './components/EditButton'
import { EventsButton } from './components/EventsButton'
import { ReportButton } from './components/ReportButton'
import { SendMessageButton } from './components/SendMessageButton'

import { useActions } from './hooks/useActions'
import { useBreadcrumbsStatus } from './hooks/useBreadcrumbsStatus'
import { useEvents } from './hooks/useEvents'
import { useFitMapOnVehiclesForBreadcrumbFetch } from './hooks/useFitMapOnVehiclesForBreadcrumbFetch'
import { useModalActions } from './hooks/useModalActions'
import { showByPermission } from './utils'
import { StyledSeparator, StyledStack } from './hooks/useStyles'

interface Props {
  expanded: boolean
  toggleExpanded: () => void
  permissions: ActionPermissions
  vehicles: uui.domain.client.UnifiedVehicle[]
}

export function Actions(props: Props) {
  const { expanded, permissions, vehicles, toggleExpanded } = props

  const breadcrumbStatus = useBreadcrumbsStatus(vehicles)
  const { modals, modalActions } = useModalActions()
  const eventsCount = useEvents(vehicles)
  const { toggleBreadcrumbs, goToEvents, centerOnMap, goToEdit, runReports } = useActions(
    vehicles,
    breadcrumbStatus,
    eventsCount,
  )

  useFitMapOnVehiclesForBreadcrumbFetch(vehicles)

  return (
    <Stack
      direction="row"
      data-trackid="navigo-vehicle-overview-actions"
      data-testid="navigo-vehicle-overview-actions"
      alignItems="center"
      width="auto"
      height="100%"
    >
      <StyledStack direction="row" height="100%" width="auto" alignItems="center">
        {expanded && (
          <>
            {showByPermission(permissions.events) && (
              <EventsButton
                permission={permissions.events}
                vehicles={vehicles}
                onClick={goToEvents}
                count={eventsCount}
              />
            )}

            {showByPermission(permissions.breadcrumbs) && (
              <BreadcrumbsButton
                permission={permissions.breadcrumbs}
                onClick={toggleBreadcrumbs}
                status={breadcrumbStatus}
                vehicles={vehicles}
              />
            )}

            <StyledSeparator />

            {showByPermission(permissions.report) && (
              <ReportButton
                permission={permissions.report}
                vehicles={vehicles}
                onClick={runReports}
              />
            )}

            {showByPermission(permissions.sendMessage) && (
              <SendMessageButton
                onClick={modalActions.showSendMessageToDriverModal}
                permission={permissions.sendMessage}
                vehicles={vehicles}
              />
            )}

            <CenterOnMapButton
              permission={permissions.centerOnMap}
              vehicles={vehicles}
              onClick={centerOnMap}
            />
          </>
        )}

        <ExpandButton expanded={expanded} toggleExpanded={toggleExpanded} />

        <EditButton onClick={goToEdit} permission={permissions.edit} />
      </StyledStack>

      {modals}
    </Stack>
  )
}
